import {
  MenuButton as MenuButtonHeadless,
  Menu as MenuHeadless,
  MenuItem as MenuItemHeadless,
  MenuItems as MenuItemsHeadless,
} from "@headlessui/react"
import { Link, type LinkProps } from "@tanstack/react-location"
import {
  Fragment,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from "react"
import { modifiers } from "ui-tools"
import { Icon } from "../Icon"
import s from "./Menu.module.scss"

export const Menu = ({ children }: { children: ReactNode }) => {
  return <MenuHeadless>{children}</MenuHeadless>
}

Menu.Trigger = ({
  asChild = false,
  children,
  dataTestId,
}: {
  asChild?: boolean
  children?: ReactNode
  dataTestId?: string
}) => {
  if (asChild) {
    return (
      <MenuButtonHeadless as={Fragment} data-testid={dataTestId}>
        {children}
      </MenuButtonHeadless>
    )
  }

  return (
    <MenuButtonHeadless
      as="button"
      className={s.menuTrigger__button}
      data-testid={dataTestId}
    >
      {({ active }) => (
        <>
          <div className={s.menuTrigger__content}>{children}</div>

          <Icon
            icon="Chevron"
            className={modifiers(s, "menuTrigger__chevron", { active })}
          />
        </>
      )}
    </MenuButtonHeadless>
  )
}

Menu.Content = ({
  children,
  anchor = "bottom",
  className,
}: {
  children: ReactNode
  className?: string
  anchor?:
    | "bottom"
    | "bottom start"
    | "bottom end"
    | "top"
    | "top start"
    | "top end"
    | "left"
    | "left start"
    | "left end"
    | "right"
    | "right start"
    | "right end"
}) => (
  <MenuItemsHeadless
    className={[s.menuContent, className].filter(Boolean).join(" ")}
    anchor={anchor}
    transition
  >
    {children}
  </MenuItemsHeadless>
)

type MenuItemProps<C extends ElementType> = {
  children: ReactNode
  isActive?: boolean
} & (({ as?: C } & ComponentPropsWithoutRef<C>) | ({ as?: "Link" } & LinkProps))
Menu.Item = <C extends ElementType = "button">({
  as,
  children,
  isActive = false,
  ...props
}: MenuItemProps<C>) => {
  const className = modifiers(s, "menuItem", { isActive })

  if (!as) {
    return <MenuItemHeadless as={Fragment}>{children}</MenuItemHeadless>
  }

  if (as === "Link") {
    return (
      <MenuItemHeadless as={Fragment}>
        <Link className={className} {...props}>
          {children}
        </Link>
      </MenuItemHeadless>
    )
  }

  const Component = (as || "div") as ElementType

  return (
    <MenuItemHeadless as={Component} className={className} {...props}>
      {children}

      {isActive && <Icon icon="CheckmarkCircle" className={s.menuItem__icon} />}
    </MenuItemHeadless>
  )
}
